const PACK_POINTER = "tt-offline-pointer";
const PACK_LEGACY = "tt-offline-pack";
const PACK_NEXT_LEGACY = "tt-offline-pack-next";
const PACK_META = "tt-offline-meta";
const OFFLINE_READER = "tt-offline-reader";
const ACTIVE_KEY = "tt-offline-active-cache";

export type PackMeta = {
  ready: boolean;
  generatedAt?: string;
  lastSync?: string;
  bytes?: number;
  error?: string;
  documentCount?: number;
  missingDocuments?: string[];
  revision?: number;
  cacheName?: string;
};

export type PackProgress = {
  step: string;
  done: number;
  total: number;
};

export function packCacheName(revision: number | string): string {
  return `tt-pack-${revision}`;
}

export function readPackMeta(): PackMeta {
  try {
    return JSON.parse(localStorage.getItem(PACK_META) ?? '{"ready":false}') as PackMeta;
  } catch {
    return { ready: false };
  }
}

export function readActivePackName(): string {
  try {
    return localStorage.getItem(ACTIVE_KEY) || PACK_LEGACY;
  } catch {
    return PACK_LEGACY;
  }
}

async function writePointer(cacheName: string) {
  localStorage.setItem(ACTIVE_KEY, cacheName);
  const pointer = await caches.open(PACK_POINTER);
  await pointer.put("/active", new Response(cacheName, { headers: { "Content-Type": "text/plain" } }));
}

async function verifyCache(cacheName: string): Promise<{ ok: boolean; generatedAt?: string; missing: string[]; revision?: number }> {
  const cache = await caches.open(cacheName);
  const pack = await cache.match("/offline-pack.json");
  if (!pack) return { ok: false, missing: ["itinerary"] };
  const data = await pack.json() as { trip?: { attachments?: Array<{ id: string; filename?: string }>; revision?: number }; generatedAt?: string };
  const missing: string[] = [];
  for (const att of data.trip?.attachments ?? []) {
    const hit = await cache.match(`/api/attachments/${att.id}`);
    if (!hit) missing.push(att.filename ?? att.id);
  }
  return { ok: missing.length === 0, generatedAt: data.generatedAt, missing, revision: data.trip?.revision };
}

export async function verifyPackReady(): Promise<{ ok: boolean; generatedAt?: string; missing: string[] }> {
  const meta = readPackMeta();
  const active = readActivePackName();
  const checked = await verifyCache(active);
  const ok = meta.ready && checked.ok;
  return { ok, generatedAt: checked.generatedAt ?? meta.generatedAt, missing: checked.missing };
}

export async function downloadPack(onProgress?: (progress: PackProgress) => void): Promise<PackMeta> {
  const stagingName = packCacheName(`next-${Date.now()}`);
  await caches.delete(stagingName);
  const next = await caches.open(stagingName);
  onProgress?.({ step: "Fetching itinerary", done: 0, total: 2 });
  const res = await fetch("/api/pack", { credentials: "same-origin" });
  if (!res.ok) throw new Error("Could not download the trip pack.");
  const data = await res.json() as {
    generatedAt?: string;
    trip?: { attachments?: Array<{ id: string; filename?: string }>; revision?: number };
  };
  await next.put("/offline-pack.json", new Response(JSON.stringify(data), { headers: { "Content-Type": "application/json" } }));
  const attachments = data.trip?.attachments ?? [];
  const total = Math.max(1, attachments.length + 1);
  let done = 1;
  let bytes = JSON.stringify(data).length;
  const missing: string[] = [];
  onProgress?.({ step: "Saving itinerary", done, total });
  for (const att of attachments) {
    const file = await fetch(`/api/attachments/${att.id}`, { credentials: "same-origin" });
    if (!file.ok) {
      missing.push(att.filename ?? att.id);
      continue;
    }
    bytes += Number(file.headers.get("content-length") ?? 0) || (await file.clone().arrayBuffer()).byteLength;
    await next.put(`/api/attachments/${att.id}`, file.clone());
    done += 1;
    onProgress?.({ step: "Saving documents", done, total });
  }
  if (missing.length) {
    await caches.delete(stagingName);
    throw new Error("The pack stopped before every document was stored. The previous pack is unchanged.");
  }
  const verified = await verifyCache(stagingName);
  if (!verified.ok) {
    await caches.delete(stagingName);
    throw new Error("The new pack could not be verified. The previous pack is unchanged.");
  }
  const revision = data.trip?.revision ?? Date.now();
  const previous = readActivePackName();
  await writePointer(stagingName);
  for (const name of await caches.keys()) {
    if ((name.startsWith("tt-pack-") || name === PACK_LEGACY) && name !== stagingName && name !== previous) {
      await caches.delete(name);
    }
  }
  await caches.delete(PACK_NEXT_LEGACY);
  const meta: PackMeta = {
    ready: true,
    generatedAt: data.generatedAt,
    lastSync: new Date().toISOString(),
    bytes,
    documentCount: attachments.length,
    missingDocuments: [],
    revision,
    cacheName: stagingName,
  };
  localStorage.setItem(PACK_META, JSON.stringify(meta));
  localStorage.setItem(OFFLINE_READER, "1");
  return meta;
}

export async function readCachedTrip() {
  const active = await caches.open(readActivePackName());
  const res = await active.match("/offline-pack.json");
  if (res) return res.json();
  const legacy = await caches.open(PACK_LEGACY);
  const fallback = await legacy.match("/offline-pack.json");
  if (fallback) return fallback.json();
  return null;
}

export async function readCachedAttachment(id: string) {
  const active = await caches.open(readActivePackName());
  const hit = await active.match(`/api/attachments/${id}`);
  if (hit) return hit;
  const legacy = await caches.open(PACK_LEGACY);
  return legacy.match(`/api/attachments/${id}`);
}

export async function clearPack() {
  for (const name of await caches.keys()) {
    if (name.startsWith("tt-pack-") || name === PACK_LEGACY || name === PACK_NEXT_LEGACY || name === PACK_POINTER) {
      await caches.delete(name);
    }
  }
  localStorage.removeItem(PACK_META);
  localStorage.removeItem(OFFLINE_READER);
  localStorage.removeItem(ACTIVE_KEY);
}

export function formatBytes(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  return `${Math.round(bytes / 1024)} KB`;
}

export function packAgeLabel(meta: PackMeta, now = new Date()): string {
  if (!meta.lastSync) return "Not downloaded on this device.";
  const then = new Date(meta.lastSync).getTime();
  const hours = Math.max(0, Math.round((now.getTime() - then) / 3_600_000));
  if (hours < 1) return "Downloaded less than an hour ago";
  if (hours === 1) return "Downloaded 1 hour ago";
  if (hours < 48) return `Downloaded ${hours} hours ago`;
  const days = Math.round(hours / 24);
  return `Downloaded ${days} days ago`;
}
